import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, FileText, Loader2, AlertTriangle, Shield, Lock } from 'lucide-react';

// Use same API base URL logic as App.js for production compatibility
const getApiBase = () => {
  const hostname = window.location.hostname;
  if (hostname === 'compliance.cyberforte.com.au' || hostname === 'www.compliance.cyberforte.com.au') {
    return '/api';
  }
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
};

export default function PolicyAcknowledgePage() {
  const { token } = useParams();
  const API = getApiBase();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [resultMsg, setResultMsg] = useState('');

  // Access-code gate (6-digit code the admin shares alongside the link).
  const [codeRequired, setCodeRequired] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verifiedCode, setVerifiedCode] = useState(null);

  const loadPolicy = (code) => {
    const query = code ? `?code=${encodeURIComponent(code)}` : '';
    return axios.get(`${API}/policy-ack/public/${token}${query}`)
      .then(res => {
        if (res.data.code_required) {
          setCodeRequired(true);
          setPolicy(null);
          if (res.data.invalid_code) setCodeError('Incorrect access code. Please try again.');
          return null;
        }
        setPolicy(res.data);
        setCodeRequired(false);
        setCodeError(null);
        if (code) setVerifiedCode(code);
        return res.data;
      });
  };

  useEffect(() => {
    loadPolicy(null)
      .catch((err) => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 404) {
          setError('This policy link is invalid or has expired. Please contact your administrator for a new link.');
        } else if (detail) {
          setError(detail);
        } else {
          setError(`Unable to load policy. Please check your connection and try again. (${err.message})`);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const trimmed = codeInput.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setCodeError('Please enter the 6-digit code from your administrator.');
      return;
    }
    setVerifyingCode(true);
    setCodeError(null);
    try {
      await loadPolicy(trimmed);
    } catch (err) {
      setCodeError(err.response?.data?.detail || 'Unable to verify code. Please try again.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/policy-ack/public/${token}/acknowledge`, {
        name, email, code: verifiedCode || undefined,
      });
      setResultMsg(res.data.message);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit acknowledgement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error && !policy) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link Not Found</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (codeRequired) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Access code required</h1>
            <p className="text-sm text-slate-600 mb-6">
              This policy link is protected. Enter the 6-digit code your administrator shared with you to continue.
            </p>
          </div>
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <Input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoFocus
              className="text-center text-2xl font-mono tracking-[0.4em] h-14"
              data-testid="ack-code-input"
            />
            {codeError && (
              <p className="text-sm text-red-600 text-center" data-testid="ack-code-error">{codeError}</p>
            )}
            <Button
              type="submit"
              disabled={verifyingCode || codeInput.length !== 6}
              className="w-full bg-slate-900 text-white hover:bg-slate-800"
              data-testid="ack-code-submit"
            >
              {verifyingCode ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : 'Continue'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Acknowledged</h1>
          <p className="text-slate-600">{resultMsg}</p>
          <p className="text-sm text-slate-500 mt-4">You may close this page.</p>
        </div>
      </div>
    );
  }

  const pdfSrc = `${API}/policy-ack/public/${token}/pdf${verifiedCode ? `?code=${encodeURIComponent(verifiedCode)}` : ''}`;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Shield className="w-6 h-6 text-cyan-400" />
          <div>
            <h1 className="text-lg font-bold">{policy.organization || 'Organization'}</h1>
            <p className="text-slate-400 text-sm">Policy Acknowledgement</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Policy Info */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-2xl font-bold text-slate-900" data-testid="policy-title">{policy.title}</h2>
          {policy.description && (
            <p className="text-slate-600 mt-2">{policy.description}</p>
          )}
          <p className="text-xs text-slate-500 mt-2">Version {policy.version}</p>
        </div>

        {/* PDF Viewer */}
        {policy.has_pdf && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="bg-slate-100 px-4 py-2 border-b flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">{policy.pdf_filename}</span>
            </div>
            <iframe
              src={pdfSrc}
              className="w-full border-0"
              style={{ height: '600px' }}
              title="Policy Document"
              data-testid="pdf-viewer"
            />
          </div>
        )}

        {/* Acknowledgement Form */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Acknowledge this Policy</h3>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700" data-testid="ack-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Full Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your full name" required data-testid="ack-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Email Address</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required data-testid="ack-email" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer pt-2">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300" data-testid="ack-checkbox" />
              <span className="text-sm text-slate-700">I confirm that I have read, understood, and agree to comply with this policy.</span>
            </label>
            <Button type="submit" disabled={!agreed || !name || !email || submitting} className="w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="ack-submit">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : 'Acknowledge Policy'}
            </Button>
          </form>
        </div>
      </main>

      <footer className="text-center py-6 text-xs text-slate-400">
        Cyber Forte Compliance Manager
      </footer>
    </div>
  );
}
